import {KIND_LABEL,isImagePreviewable,opensInViewer} from '../lib/document-kinds.js';
import {revisionParts} from '../lib/revision-order.js';
const $ = id => document.getElementById(id);
const searchText=item=>[item.name,item.meta?.drawingNo,item.meta?.title,item.package?.path].filter(Boolean).join(' ').toLocaleLowerCase();
const byName=(a,b)=>a.name.localeCompare(b.name,'zh-Hant',{numeric:true});
/** 資料夾路徑（「結構 / 1F」）；null 或找不到＝專案根，回空字串。 */
export function folderPath(folders,id){
  const parts=[],seen=new Set();
  for(let f=folders.find(x=>x.id===id);f&&!seen.has(f.id);f=folders.find(x=>x.id===f.parentId)){seen.add(f.id);parts.unshift(f.name);}
  return parts.join(' / ');
}
/** 資料夾本身與所有子資料夾的 id。 */
export function folderSubtree(folders,id){
  const ids=new Set([id]);
  for(let grew=true;grew;){grew=false;for(const f of folders)if(f.parentId&&ids.has(f.parentId)&&!ids.has(f.id)){ids.add(f.id);grew=true;}}
  return ids;
}
/**
 * 畫出單一專案內的資料夾樹。第一列是專案根目錄。
 * 資料夾預設展開到目前選取節點；點三角形收合／展開，點名稱選取。
 */
export function renderFolderTree(container,{folders,projectId,selected,onSelect,collapsed=new Set(),rootLabel='全部'}){
  container.replaceChildren();
  const own=folders.filter(f=>f.projectId===projectId);
  const node=(folder,depth)=>{
    const li=document.createElement('li'),row=document.createElement('div'),toggle=document.createElement('button'),button=document.createElement('button');
    const children=folder?own.filter(f=>f.parentId===folder.id).sort(byName):[];
    row.className='folder-row';row.style.setProperty('--depth',depth);
    toggle.type='button';toggle.className='folder-toggle';
    if(children.length){const open=!collapsed.has(folder.id);toggle.textContent=open?'▾':'▸';toggle.setAttribute('aria-label',open?'收合':'展開');toggle.setAttribute('aria-expanded',String(open));toggle.onclick=()=>{open?collapsed.add(folder.id):collapsed.delete(folder.id);renderFolderTree(container,{folders,projectId,selected,onSelect,collapsed,rootLabel});};}
    else{toggle.disabled=true;toggle.setAttribute('aria-hidden','true');}
    button.type='button';button.className='folder-node';button.dataset.folder=folder?.id??'';
    const active=(folder?.id??null)===selected;button.classList.toggle('is-active',active);button.setAttribute('aria-current',active?'true':'false');
    button.innerHTML=`<svg class="folder-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="${folder?'M3 6h6l2 2h10v11H3z':'M4 4h16v16H4zM4 9h16M9 9v11'}"/></svg>`;
    const label=document.createElement('span');label.className='folder-name';label.textContent=folder?folder.name:rootLabel;button.append(label);
    if(folder?.itemCount){const n=document.createElement('small');n.textContent=String(folder.itemCount);button.append(n);}
    button.title=folder?folderPath(folders,folder.id):rootLabel;
    button.onclick=()=>onSelect(folder?.id??null);
    row.append(toggle,button);li.append(row);
    if(children.length&&!collapsed.has(folder.id)){const ul=document.createElement('ul');for(const child of children)ul.append(node(child,depth+1));li.append(ul);}
    return li;
  };
  const ul=document.createElement('ul');ul.className='folder-tree-list';ul.setAttribute('role','tree');
  const root=node(null,0);ul.append(root);
  for(const folder of own.filter(f=>!f.parentId).sort(byName))ul.append(node(folder,1));
  container.append(ul);
}

/**
 * 文件庫完整目錄樹：頂層是「全部／未分類／各目錄（專案）」，其下才是子資料夾。
 * onSelect({ project, folder })；project 為 'all'｜'unassigned'｜專案 id。
 */
/**
 * expanded：有子層的節點 id（資料夾 id 或 `p:<專案id>`）；預設空集合＝全部收攏。
 * only：只畫該專案（或 'unassigned'）；第一列是專案根，其下直接列頂層資料夾。
 */
export function renderLibraryTree(container,{projects,folders,selectedProject,selectedFolder,onSelect,expanded=new Set(),only=null}){
  const rerender=()=>renderLibraryTree(container,{projects,folders,selectedProject,selectedFolder,onSelect,expanded,only});
  container.replaceChildren();
  const key=id=>`p:${id}`;
  const node=({project,folder=null,label,depth,children,count,path})=>{
    const li=document.createElement('li'),row=document.createElement('div'),toggle=document.createElement('button'),button=document.createElement('button');
    const collapseId=folder?.id||key(project);
    row.className='folder-row';row.style.setProperty('--depth',depth);
    toggle.type='button';toggle.className='folder-toggle';
    if(children.length){const open=expanded.has(collapseId);toggle.textContent=open?'▾':'▸';toggle.setAttribute('aria-label',open?'收合':'展開');toggle.setAttribute('aria-expanded',String(open));toggle.onclick=()=>{open?expanded.delete(collapseId):expanded.add(collapseId);rerender();};}
    else{toggle.disabled=true;toggle.setAttribute('aria-hidden','true');}
    button.type='button';button.className='folder-node';
    button.dataset.project=project;button.dataset.folder=folder?.id??'';
    const active=selectedProject===project&&(selectedFolder??null)===(folder?.id??null);
    button.classList.toggle('is-active',active);button.setAttribute('aria-current',active?'true':'false');
    const isRoot=folder==null;
    button.innerHTML=`<svg class="folder-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="${isRoot&&(project==='all'||project==='unassigned')?'M4 4h16v16H4zM4 9h16M9 9v11':'M3 6h6l2 2h10v11H3z'}"/></svg>`;
    const name=document.createElement('span');name.className='folder-name';name.textContent=label;button.append(name);
    if(folder?.executionUpgraded){const badge=document.createElement('span');badge.className='folder-execution-badge';badge.textContent='已升級為執行版';button.append(badge);}
    if(count){const n=document.createElement('small');n.textContent=String(count);button.append(n);}
    button.title=path||label;
    button.onclick=()=>onSelect({project,folder:folder?.id??null});
    row.append(toggle,button);li.append(row);
    if(children.length&&expanded.has(collapseId)){const ul=document.createElement('ul');for(const child of children)ul.append(child);li.append(ul);}
    return li;
  };
  const folderNodes=(projectId,parentId,depth)=>{
    const own=folders.filter(f=>f.projectId===projectId&&(f.parentId??null)===parentId).sort(byName);
    return own.map(folder=>node({
      project:projectId,folder,label:folder.name,depth,
      children:folderNodes(projectId,folder.id,depth+1),
      count:folder.itemCount,path:folderPath(folders,folder.id),
    }));
  };
  const ul=document.createElement('ul');ul.className='folder-tree-list';ul.setAttribute('role','tree');
  if(only){
    const project=projects.find(p=>p.id===only);
    const label=project?.name||'未分類';
    ul.append(node({project:only,label,depth:0,children:[],count:project?.itemCount,path:label}));
    if(project)ul.append(...folderNodes(project.id,null,1));
    container.append(ul);return;
  }
  ul.append(node({project:'all',label:'全部',depth:0,children:[],count:0,path:'全部'}));
  ul.append(node({project:'unassigned',label:'未分類',depth:0,children:[],count:0,path:'未分類'}));
  for(const project of [...projects].sort(byName)){
    ul.append(node({
      project:project.id,label:project.name,depth:0,
      children:folderNodes(project.id,null,1),
      count:project.itemCount,path:project.name,
    }));
  }
  container.append(ul);
}
/** 依圖說資料的樓層分組；未設定樓層的放最後。 */
export function groupByFloor(items){
  const groups=new Map();
  for(const item of items){const key=item.meta?.floor||'';if(!groups.has(key))groups.set(key,[]);groups.get(key).push(item);}
  const label=item=>item.meta?.drawingNo||item.name;
  return [...groups].sort(([a],[b])=>!a-!b||a.localeCompare(b,'zh-Hant',{numeric:true}))
    .map(([floor,list])=>({floor,items:list.sort((a,b)=>label(a).localeCompare(label(b),'zh-Hant',{numeric:true}))}));
}
/**
 * 依專案、資料夾（含子資料夾）、搜尋與類型篩選。
 * kind：all｜drawings｜models｜pdf｜images｜documents（Office／附件／其他；可轉 PDF 的 Office 可開啟，其餘與 HEIC 下載）。
 */
export function catalogItems(items, {project = 'all', query = '', kind = 'all', folder = null, folders = [], formatOnly = false} = {}) {
  const subtree=folder?folderSubtree(folders,folder):null;
  return items.filter(item => {
    if(project==='unassigned'&&item.projectId)return false;
    if(!['all','unassigned'].includes(project)&&item.projectId!==project)return false;
    if(subtree&&!subtree.has(item.folderId))return false;
    if(query&&!searchText(item).includes(query.toLocaleLowerCase()))return false;
    if(kind==='pdf')return item.kind==='pdf';
    if(kind==='images')return item.kind==='image'&&isImagePreviewable(item.name);
    if(kind==='documents')return ['office','attachment','other','web','sheetset'].includes(item.kind)||(item.kind==='image'&&!isImagePreviewable(item.name));
    if(formatOnly&&kind==='drawings')return item.kind==='drawing';
    if(formatOnly&&kind==='models')return item.kind==='model';
    const engines=Object.values(item.engines||{}),revit=/\.r(vt|fa)$/i.test(item.name);
    if(kind==='drawings')return !revit&&(engines.some(e=>e.hasSvg)||engines.some(e=>['pending','converting'].includes(e.status)));
    if(kind==='models')return revit||engines.some(e=>e.hasMesh3d)||engines.some(e=>['pending','converting'].includes(e.status));
    return true;
  });
}

export class LocationNavigation {
  constructor({state,activeItem,openItem,enter2D,enter3D,exit3D,linked,showReviewPanel,flash,engine,selectFolder}) {
    Object.assign(this,{state,activeItem,openItem,enter2D,enter3D,exit3D,linked,showReviewPanel,flash,engine,selectFolder});this.kind='drawings';this.query='';this.expanded=new Set();
    $('locationSearch').oninput=e=>{this.query=e.target.value;this.renderCatalog();};
    $('locationProject').onchange=e=>this.selectFolder?.({project:e.target.value,folder:null});
    for(const button of document.querySelectorAll('[data-location-kind]'))button.onclick=()=>{this.kind=button.dataset.locationKind;this.renderCatalog();};
    $('locationFloorManage').onclick=()=>this.openGroup('objects').catch(e=>flash(e.message));
    $('locationFloor').onchange=()=>{const target=$('v3dFloorList');target.value=$('locationFloor').value;target.dispatchEvent(new Event('change',{bubbles:true}));this.sync();};
    const action=(id,fn)=>$(id).onclick=()=>Promise.resolve().then(fn).catch(e=>flash(e.message));
    action('viewMode2D',async()=>{this.linked().cancel();this.linked().setSplit(false);await enter2D();this.sync();});
    action('viewMode3D',async()=>{this.linked().cancel();this.linked().setSplit(false);await enter3D({manual:true});this.sync();});
    action('viewModeSplit',async()=>{
      const id=state.activeId;showReviewPanel('linked');const linked=this.linked();if(linked.split)return;
      await enter3D({manual:true});if(state.activeId!==id||!state.mode3d)return;
      await linked.loaded;if(state.activeId!==id)return;
      linked.refreshChoices();
      if(!linked.drawing.hasContent){
        const current=this.item(),saved=linked.data.links[0];
        const drawing=saved&&state.items.find(i=>i.id===saved.drawingId)||catalogItems(state.items,{project:state.selectedProject,kind:'drawings'}).find(i=>Object.values(i.engines||{}).some(e=>e.hasSvg));
        const chosen=current?.engines?.[engine()]?.hasSvg?current:drawing;
        if(!chosen)throw new Error('請先匯入並轉好一張 2D 圖面，再設定 2D／3D 並列');
        $('linkedDrawing').value=chosen.id;$('linkedEngine').value='oda';if(saved?.drawingId===chosen.id)$('linkedFloor').value=saved.floorName;
      }
      await linked.toggleSplit();this.sync();
    });
    for(const button of document.querySelectorAll('.v3d-group-tab'))button.addEventListener('click',()=>this.sync());
  }
  item(){return this.activeItem?.() ?? this.state.items.find(i=>i.id===this.state.activeId);}
  /** 相對目前瀏覽位置的路徑；同名檔案靠這行區分。過長時保留最後兩層。 */
  relativePath(item){
    const {projects,folders,selectedProject,selectedFolder}=this.state;
    const parts=[];
    if(item.projectId){
      if(selectedProject==='all'||!selectedProject)parts.push(projects.find(p=>p.id===item.projectId)?.name||'');
      const chain=[];for(let f=folders.find(x=>x.id===item.folderId),seen=new Set();f&&!seen.has(f.id);f=folders.find(x=>x.id===f.parentId)){seen.add(f.id);if(f.id===selectedFolder)break;chain.unshift(f.name);}
      parts.push(...chain);
    }else if(selectedProject==='all'||!selectedProject)parts.push('未分類');
    const clean=parts.filter(Boolean);
    return {full:clean.join(' / '),short:clean.length>2?`… / ${clean.slice(-2).join(' / ')}`:clean.join(' / ')};
  }
  /** 檢視側欄可選的專案：各頂層目錄；有未分類檔案（或尚無目錄）時加「未分類」。 */
  projectChoices(){
    const {projects,items}=this.state;
    const choices=[...projects].sort(byName).map(p=>({id:p.id,label:p.itemCount?`${p.name} (${p.itemCount})`:p.name}));
    if(!projects.length||items.some(i=>!i.projectId))choices.push({id:'unassigned',label:'未分類'});
    return choices;
  }
  /** 檢視一定要落在單一專案：「全部」或已不存在時，改選開啟中檔案的專案，否則第一個。 */
  ensureProject(){
    const choices=this.projectChoices(),{selectedProject}=this.state;
    if(choices.some(c=>c.id===selectedProject))return;
    const active=this.item(),fallback=active?(active.projectId||'unassigned'):choices[0]?.id;
    const project=choices.some(c=>c.id===fallback)?fallback:choices[0]?.id;
    if(project&&this.selectFolder)this.selectFolder({project,folder:null});
  }
  renderFolders(){
    const select=$('locationProject'),choices=this.projectChoices();
    select.replaceChildren(...choices.map(c=>new Option(c.label,c.id)));
    const current=choices.some(c=>c.id===this.state.selectedProject)?this.state.selectedProject:'';
    select.value=current;select.disabled=!choices.length;
    const settings=$('locationProjectSettings');
    if(settings)settings.hidden=!this.state.projects.some(p=>p.id===current)||!window.dcmsAuth?.can('project.members.read',{projectId:current,folderId:null});
    $('locationFolders').hidden=!current;
    if(!current){$('locationFolderTree').replaceChildren();return;}
    renderLibraryTree($('locationFolderTree'),{
      projects:this.state.projects,folders:this.state.folders,
      selectedProject:this.state.selectedProject,selectedFolder:this.state.selectedFolder,
      onSelect:loc=>this.selectFolder?this.selectFolder(loc):null,expanded:this.expanded,only:current,
    });
  }
  renderCatalog(){
    if(this.state.workspace==='viewer')this.ensureProject();
    this.renderFolders();
    const list=$('locationItems');list.replaceChildren();
    for(const button of document.querySelectorAll('[data-location-kind]')){const selected=button.dataset.locationKind===this.kind;button.classList.toggle('is-active',selected);button.setAttribute('aria-pressed',String(selected));}
    const items=catalogItems(this.state.items,{project:this.state.selectedProject,query:this.query,kind:this.kind,folder:this.state.selectedFolder,folders:this.state.folders});
    if(!items.length){const li=document.createElement('li');li.className='location-empty';li.textContent='沒有符合的內容';list.append(li);}
    const groups=groupByFloor(items),labelled=groups.some(g=>g.floor);
    for(const group of groups)for(const [index,item] of group.items.entries()){
      if(labelled&&index===0){const head=document.createElement('li');head.className='location-group';head.textContent=group.floor||'未設定樓層';list.append(head);}
      const li=document.createElement('li'),button=document.createElement('button'),head=document.createElement('span'),name=document.createElement('span'),type=document.createElement('small');button.type='button';button.dataset.locationItem=item.id;button.className='location-item';
      const no=item.meta?.drawingNo,title=item.meta?.title;name.textContent=no||title?[no,title].filter(Boolean).join(' '):item.name;name.className='location-item-name';
      const download=!opensInViewer(item);
      const revision=revisionParts(item.meta?.revision);
      type.textContent=[revision.number&&`Rev ${revision.number}`,(item.ext||'').slice(1).toUpperCase()||'無副檔名',download&&'下載'].filter(Boolean).join(' · ');
      if(revision.description){const description=document.createElement('span');description.className='revision-description';description.textContent=`狀態：${revision.description}`;type.append(description);}
      head.className='location-item-head';head.append(name,type);button.append(head);
      const where=this.relativePath(item);
      if(where.full){const path=document.createElement('span');path.className='location-item-path';path.textContent=where.short;button.append(path);}
      button.title=[item.name,where.full].filter(Boolean).join('\n');
      if(download){button.title=`${item.name}（${KIND_LABEL[item.kind]||'其他'}，點選下載）${where.full?`\n${where.full}`:''}`;button.classList.add('is-download');}
      const selected=item.id===this.state.activeId;button.classList.toggle('is-active',selected);button.setAttribute('aria-current',selected?'true':'false');button.disabled=Boolean(item.missing);
      button.onclick=download?()=>{const a=document.createElement('a');a.href=`/api/library/${item.id}/file`;a.download=item.name;a.click();}:async()=>{const kind=this.kind;try{await this.openItem(item);if(this.state.activeId!==item.id)return;this.kind=kind;this.renderCatalog();if(kind==='models'&&!this.state.mode3d)await this.enter3D();this.sync();}catch(e){this.flash(e.message);}};
      li.append(button);list.append(li);
    }
  }
  async openGroup(group){
    if(!this.item())return;const id=this.state.activeId;
    if(group==='measure'&&!this.state.mode3d&&this.state.has2d){this.showReviewPanel('measure2d');return;}
    if(!this.state.mode3d)await this.enter3D({manual:true});if(!this.state.mode3d||!this.state.viewer3d?.bbox||this.state.activeId!==id)return;
    const target=$({view:'v3dViewGroup',objects:'v3dObjectsGroup',section:'v3dSectionGroup',measure:'v3dMeasureGroup',notes:'v3dNotesGroup'}[group]);
    if(target.getAttribute('aria-expanded')!=='true')target.click();this.sync();
  }
  sync(){
    const item=this.item(),linked=this.linked(),split=Boolean(linked?.split),ready=Boolean(item),doc=Boolean(this.state.docMode),modelReady=ready&&this.state.mode3d&&Boolean(this.state.viewer3d?.bbox);
    $('viewerEmpty').hidden=ready;$('btnPrint').disabled=!ready||this.state.docMode==='sheetset';$('locationCurrent').textContent=item?.name||'尚未開啟圖面或模型';
    const mode=split?'Split':this.state.mode3d?'3D':'2D';
    for(const [id,value]of [['viewMode2D','2D'],['viewMode3D','3D'],['viewModeSplit','Split']]){const button=$(id),active=ready&&!doc&&value===mode;button.classList.toggle('is-active',active);button.setAttribute('aria-pressed',String(active));button.disabled=!ready||doc;}
    $('viewMode2D').title=doc?(this.state.docMode==='sheetset'?'圖紙集以清冊預覽，沒有 2D／3D 模式':this.state.docMode==='web'?'網頁沒有 2D／3D 模式':this.state.docMode==='image'?'圖片預覽沒有 2D／3D 模式':'PDF 以頁面預覽，沒有 2D／3D 模式'):this.state.has2d?'切換 2D 圖面':'切換 2D；尚無圖面時嘗試轉檔（RVT／RFA 尚不支援）';
    const a=this.state.viewer3d?.advanced,floors=a?.config.floors||[],signature=JSON.stringify(floors)+modelReady+a?.floor?.id;
    if(signature!==this.floorSignature){this.floorSignature=signature;const select=$('locationFloor');select.replaceChildren(new Option('全部樓層',''));if(modelReady)for(const floor of floors){const b=this.state.viewer3d.bbox,outside=floor.maxZ<b.min[2]||floor.minZ>b.max[2];select.add(new Option(floor.name+(outside?'（範圍外）':''),floor.id));}select.value=modelReady?a?.floor?.id||'':'';}
    $('locationFloor').disabled=!modelReady;$('locationFloorManage').disabled=!ready;
    for(const button of document.querySelectorAll('[data-review]'))button.disabled=!ready||doc;
    const tools=$('viewerTools'),toolMode=doc?'document':split?'split':this.state.mode3d?'3d':'2d';
    if(tools&&tools.dataset.mode!==toolMode){tools.dataset.mode=toolMode;for(const button of tools.querySelectorAll('[data-needs3d]')){button.dataset.baseTitle??=button.title;button.title=button.id==='v3dNotesGroup'&&(doc||!this.state.mode3d&&this.state.has2d)?(doc?'PDF／圖片頁面釋疑':'2D 圖面釋疑'):toolMode==='2d'?`${button.dataset.baseTitle}（按下會切換到 3D）`:button.dataset.baseTitle;}}
    document.querySelector('.tb-more').hidden=doc;
    document.querySelector('.view-float').hidden=!ready||['web','sheetset'].includes(this.state.docMode);
    for(const button of document.querySelectorAll('[data-location-item]')){const active=button.dataset.locationItem===this.state.activeId;button.classList.toggle('is-active',active);button.setAttribute('aria-current',String(active));}
  }
}
